import React, { useState, useEffect } from 'react';
import { serviceAPI } from '../../services/api';
import { RefreshCw, Clock, AlertCircle, CheckCircle } from 'lucide-react';
import { toast } from 'react-toastify';

const PricingStatusCard = ({ isDarkMode = false }) => {
  const [pricingStatus, setPricingStatus] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPricingStatus();
  }, []);

  // Debug effect to log state changes
  useEffect(() => {
    console.log('🔄 PricingStatusCard state updated:', pricingStatus);
  }, [pricingStatus]);

  const fetchPricingStatus = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching pricing status...');
      const response = await serviceAPI.getPricingStatus();
      console.log('📊 Pricing status response:', response);
      if (response.success) {
        setPricingStatus(response.data);
        console.log('✅ Pricing status updated:', response.data);
      }
    } catch (error) {
      console.error('Error fetching pricing status:', error);
      toast.error('Failed to fetch pricing status');
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshPricing = async () => {
    try {
      setIsRefreshing(true);
      console.log('🔄 Starting pricing refresh...');
      const response = await serviceAPI.refreshPricingFromWebsite();
      console.log('🔄 Refresh response:', response);
      if (response.success) {
        toast.success('Pricing updated successfully from website');
        console.log('✅ Refresh successful, fetching updated status...');
        // Wait a bit for backend to update cache, then fetch status
        await new Promise(resolve => setTimeout(resolve, 1000));
        await fetchPricingStatus(); // Refresh the status
      } else {
        toast.error('Failed to refresh pricing');
      }
    } catch (error) {
      console.error('Error refreshing pricing:', error);
      toast.error('Failed to refresh pricing from website');
    } finally {
      setIsRefreshing(false);
    }
  };

  const formatLastUpdated = (timestamp) => {
    if (!timestamp) return 'Never';
    
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours} hours ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} days ago`;
  };

  const getStatusColor = (pricingStatus) => {
    if (!pricingStatus) {
      return isDarkMode ? 'text-skyone-muted-dark bg-skyone-inset-dark' : 'text-gray-600 bg-gray-50';
    }
    
    if (pricingStatus.hasData && !pricingStatus.isStale) {
      return isDarkMode ? 'text-green-400 bg-green-900/20' : 'text-green-600 bg-green-50';
    } else if (pricingStatus.hasData && pricingStatus.isStale) {
      return isDarkMode ? 'text-yellow-400 bg-yellow-900/20' : 'text-yellow-600 bg-yellow-50';
    } else {
      return isDarkMode ? 'text-red-400 bg-red-900/20' : 'text-red-600 bg-red-50';
    }
  };

  const getStatusIcon = (pricingStatus) => {
    if (!pricingStatus) return <Clock className="w-4 h-4" />;
    
    if (pricingStatus.hasData && !pricingStatus.isStale) {
      return <CheckCircle className="w-4 h-4" />;
    } else if (pricingStatus.hasData && pricingStatus.isStale) {
      return <AlertCircle className="w-4 h-4" />;
    } else {
      return <AlertCircle className="w-4 h-4" />;
    }
  };

  const getStatusText = (pricingStatus) => {
    if (!pricingStatus) return 'Unknown';
    
    if (pricingStatus.hasData && !pricingStatus.isStale) {
      return 'Up to date';
    } else if (pricingStatus.hasData && pricingStatus.isStale) {
      return 'Stale data';
    } else {
      return 'No data';
    }
  };

  if (loading || !pricingStatus) {
    return (
      <div className={`rounded-lg shadow-sm border p-6 transition-colors duration-300 ${
        isDarkMode ? 'bg-skyone-card-dark border-skyone-border-dark' : 'bg-white border-gray-200'
      }`}>
        <div className="animate-pulse">
          <div className={`h-4 rounded w-1/3 mb-4 transition-colors duration-300 ${
            isDarkMode ? 'bg-skyone-inset-dark' : 'bg-gray-200'
          }`}></div>
          <div className={`h-3 rounded w-1/2 mb-2 transition-colors duration-300 ${
            isDarkMode ? 'bg-skyone-inset-dark' : 'bg-gray-200'
          }`}></div>
          <div className={`h-3 rounded w-2/3 transition-colors duration-300 ${
            isDarkMode ? 'bg-skyone-inset-dark' : 'bg-gray-200'
          }`}></div>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-lg shadow-sm border p-6 transition-colors duration-300 ${
      isDarkMode ? 'bg-skyone-card-dark border-skyone-border-dark' : 'bg-white border-gray-200'
    }`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className={`text-lg font-semibold transition-colors duration-300 ${
          isDarkMode ? 'text-white' : 'text-gray-900'
        }`}>Pricing Status</h3>
        <button
          onClick={handleRefreshPricing}
          disabled={isRefreshing}
          className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
            isRefreshing
              ? isDarkMode 
                ? 'bg-skyone-inset-dark text-gray-500 cursor-not-allowed'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          <span>{isRefreshing ? 'Updating...' : 'Refresh'}</span>
        </button>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className={`text-sm transition-colors duration-300 ${
            isDarkMode ? 'text-gray-400' : 'text-gray-600'
          }`}>Status:</span>
          <div className={`flex items-center space-x-2 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(pricingStatus)}`}>
            {getStatusIcon(pricingStatus)}
            <span>{getStatusText(pricingStatus)}</span>
            {isRefreshing && <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>}
          </div>
        </div>

        <div className="flex items-center justify-between">
          <span className={`text-sm transition-colors duration-300 ${
            isDarkMode ? 'text-gray-400' : 'text-gray-600'
          }`}>Source:</span>
          <span className={`text-sm font-medium capitalize transition-colors duration-300 ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>
            {pricingStatus?.source || 'Unknown'}
          </span>
        </div>

        <div className="flex items-center justify-between">
          <span className={`text-sm transition-colors duration-300 ${
            isDarkMode ? 'text-gray-400' : 'text-gray-600'
          }`}>Last Updated:</span>
          <span className={`text-sm font-medium transition-colors duration-300 ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>
            {formatLastUpdated(pricingStatus?.lastUpdated)}
          </span>
        </div>

        {pricingStatus?.hoursUntilRefresh !== null && (
          <div className="flex items-center justify-between">
            <span className={`text-sm transition-colors duration-300 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-600'
            }`}>Next Refresh:</span>
            <span className={`text-sm font-medium transition-colors duration-300 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              {pricingStatus.hoursUntilRefresh > 0 
                ? `In ${pricingStatus.hoursUntilRefresh} hours`
                : 'Due now'
              }
            </span>
          </div>
        )}

        {pricingStatus?.fetchAttempts > 0 && (
          <div className="flex items-center justify-between">
            <span className={`text-sm transition-colors duration-300 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-600'
            }`}>Fetch Attempts:</span>
            <span className={`text-sm font-medium transition-colors duration-300 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              {pricingStatus.fetchAttempts}
            </span>
          </div>
        )}

        <div className={`pt-3 border-t transition-colors duration-300 ${
          isDarkMode ? 'border-skyone-border-dark' : 'border-gray-200'
        }`}>
          <div className={`flex items-center space-x-2 text-xs transition-colors duration-300 ${
            isDarkMode ? 'text-gray-500' : 'text-gray-500'
          }`}>
            <Clock className="w-3 h-3" />
            <span>Auto-refresh every 24 hours</span>
          </div>
        </div>
      </div>

      {!pricingStatus?.hasData && (
        <div className={`mt-4 p-3 rounded-md transition-colors duration-300 ${
          isDarkMode ? 'bg-red-900/20 border-red-700' : 'bg-red-50 border-red-200'
        } border`}>
          <div className="flex items-center space-x-2">
            <AlertCircle className={`w-4 h-4 transition-colors duration-300 ${
              isDarkMode ? 'text-red-400' : 'text-red-600'
            }`} />
            <span className={`text-sm transition-colors duration-300 ${
              isDarkMode ? 'text-red-200' : 'text-red-800'
            }`}>
              No pricing data available. Click refresh to fetch latest prices from website.
            </span>
          </div>
        </div>
      )}

      {pricingStatus?.hasData && pricingStatus?.isStale && (
        <div className={`mt-4 p-3 rounded-md transition-colors duration-300 ${
          isDarkMode ? 'bg-yellow-900/20 border-yellow-700' : 'bg-yellow-50 border-yellow-200'
        } border`}>
          <div className="flex items-center space-x-2">
            <AlertCircle className={`w-4 h-4 transition-colors duration-300 ${
              isDarkMode ? 'text-yellow-400' : 'text-yellow-600'
            }`} />
            <span className={`text-sm transition-colors duration-300 ${
              isDarkMode ? 'text-yellow-200' : 'text-yellow-800'
            }`}>
              Pricing data is stale (older than 24 hours). Click refresh to update.
            </span>
          </div>
        </div>
      )}

      {pricingStatus?.lastError && (
        <div className={`mt-4 p-3 rounded-md transition-colors duration-300 ${
          isDarkMode ? 'bg-red-900/20 border-red-700' : 'bg-red-50 border-red-200'
        } border`}>
          <div className="flex items-center space-x-2">
            <AlertCircle className={`w-4 h-4 transition-colors duration-300 ${
              isDarkMode ? 'text-red-400' : 'text-red-600'
            }`} />
            <span className={`text-sm transition-colors duration-300 ${
              isDarkMode ? 'text-red-200' : 'text-red-800'
            }`}>
              Last error: {pricingStatus.lastError}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default PricingStatusCard;

